export interface Profil { id: string; email: string; nom: string; role: string | null }
export interface Moi { utilisateur: Profil; environnement: string; version: string }
export interface Bascule { module: string; moteur: 'ancien' | 'v2'; modifie_le: string }

export interface Taux {
  delivrabilite: number | null; rebond: number | null; ouverture: number | null; clic: number | null;
  reactivite: number | null; reponse: number | null; positif: number | null; desinscription: number | null;
}
export interface LigneCampagne {
  id: number; nom: string; statut: string | null; envoyes: number; en_attente: number; cibles: number | null; destinataires: number;
  contactes: number; rebonds: number; ouvreurs: number; cliqueurs: number; repondants: number; automatiques: number;
  repondants_total: number; positives: number; desinscrits: number; dernier: string | null; taux: Taux;
}
export interface Indicateurs {
  perimetre: { campaign_id: number | null; nom: string | null; statut: string | null };
  volumes: { cibles: number | null; envoyes: number; delivres: number; rebonds: number; echecs: number; en_attente: number; ecartes: number;
    destinataires: number; contactes: number; premier: string | null; dernier: string | null; autres_statuts: Record<string, number> };
  engagement: { ouvreurs: number; cliqueurs: number };
  reponses: { repondants: number; humains: number; positives: number; neutres: number; negatives: number; automatiques: number;
    non_classees: number; autres: number; types: { type: string; n: number }[] };
  desinscriptions: { n: number; dnc_parmi_contactes: number };
  taux: Taux;
  relances: { nom?: string | null; statut?: string | null; envoyees: number; echecs: number; ouvreurs: number; programmees: number;
    en_pause?: number; prochaine: string | null; derniere?: string | null } | null;
  boites: { id: number | null; email: string; envoyes: number; rebonds: number; taux_rebond: number | null; ouverture: number | null; repondants: number }[];
  campagnes: LigneCampagne[] | null;
  alertes: { niveau: 'critique' | 'attention' | 'info'; texte: string }[];
}
export interface Definition { libelle: string; definition: string }
export type Definitions = Record<string, Definition>;
export interface Campagne { id: number; nom: string; statut: string | null }
export interface Evenement {
  quand: string; type: string; campagne_id: number | null; campagne: string | null; prospect_id: number | null;
  contact: string | null; email: string | null; detail: string | null; ref: string;
}
export interface PageJournal {
  evenements: Evenement[]; total: number; par_type: Record<string, number>;
  sans_date: { envois: number; echecs: number; relances_echec: number }; page: { limite: number; decalage: number };
}
export interface EtatParite {
  requis: number; jours_consecutifs: number; condition_remplie: boolean;
  derniers: { jour: string; compare_le: string; identique: boolean; comparaisons: number; nb_ecarts: number; indisponible: string | null; declencheur: string }[];
  ecarts_recents: { jour: string; ecarts: { comparaison?: string; chemin: string; ancien: unknown; v2: unknown }[] } | null;
}

export class ErreurApi extends Error {
  constructor(public readonly statut: number, public readonly code: string, public readonly detail?: string) {
    super(detail ?? code);
  }
}

async function appel<T>(methode: 'GET' | 'POST', url: string, corps?: unknown): Promise<T> {
  const r = await fetch(url, {
    method: methode,
    credentials: 'same-origin',
    headers: corps === undefined ? {} : { 'Content-Type': 'application/json' },
    body: corps === undefined ? undefined : JSON.stringify(corps),
  });
  const donnees = (await r.json().catch(() => ({}))) as { erreur?: string; detail?: string };
  if (!r.ok) throw new ErreurApi(r.status, donnees.erreur ?? 'erreur', donnees.detail);
  return donnees as T;
}

export interface Filtre { campagne?: number | null; du?: string | null; au?: string | null; types?: string[]; limite?: number; decalage?: number }

/** Chaîne de requête : seuls les paramètres renseignés sont envoyés (l'API refuse les inconnus). */
export function requete(f: Filtre): string {
  const p = new URLSearchParams();
  if (f.campagne) p.set('campagne', String(f.campagne));
  if (f.du) p.set('du', f.du);
  if (f.au) p.set('au', f.au);
  if (f.types && f.types.length) p.set('types', f.types.join(','));
  if (f.limite) p.set('limite', String(f.limite));
  if (f.decalage) p.set('decalage', String(f.decalage));
  const s = p.toString();
  return s ? `?${s}` : '';
}

export const api = {
  moi: () => appel<Moi>('GET', '/api/v2/moi'),
  connexion: (email: string, motDePasse: string) => appel<{ utilisateur: Profil }>('POST', '/api/v2/connexion', { email, motDePasse }),
  deconnexion: () => appel<{ ok: boolean }>('POST', '/api/v2/deconnexion'),
  bascule: () => appel<{ modules: Bascule[] }>('GET', '/api/v2/bascule'),
  campagnes: () => appel<{ campagnes: Campagne[] }>('GET', '/api/v2/campagnes'),
  indicateurs: (f: Filtre) => appel<{ indicateurs: Indicateurs; definitions: Definitions }>('GET', `/api/v2/indicateurs${requete(f)}`),
  journal: (f: Filtre) => appel<PageJournal>('GET', `/api/v2/journal${requete(f)}`),
  definitions: () => appel<{ definitions: Definitions; types_evenement: Record<string, string> }>('GET', '/api/v2/definitions'),
  parite: () => appel<EtatParite>('GET', '/api/v2/parite'),
  lancerParite: () => appel<{ identique: boolean; comparaisons: number; indisponible: string | null }>('POST', '/api/v2/parite'),
  basculer: (module: string, moteur: 'ancien' | 'v2') => appel<{ ok: boolean }>('POST', `/api/v2/bascule/${module}`, { moteur }),
};
